import React from 'react';
import { ArrowRight, Shield, Users, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Link } from 'react-router-dom';

const HomePage = ({ language }) => {
  const translations = {
    de: {
      hero: {
        title: 'Professionelle IT-Lösungen',
        subtitle: 'für Privatkunden & Unternehmen',
        description: 'Martinez IT Systems – Ihr zuverlässiger Partner für moderne IT-Dienstleistungen. Von der Installation bis zur Wartung, von Windows 11 bis zur Cloud-Lösung.',
        cta: 'Jetzt Kontakt aufnehmen',
        learnMore: 'Mehr erfahren'
      },
      services: {
        title: 'Meine Dienstleistungen',
        subtitle: 'Maßgeschneiderte IT-Services für Ihre Anforderungen',
        private: {
          title: 'Für Privatkunden',
          description: 'Windows 11 Installation, PC-Einrichtung, Virenschutz, WLAN-Setup und Smart Home Support',
          link: 'Mehr erfahren'
        },
        business: {
          title: 'Für Unternehmen',
          description: 'Systempflege, Netzwerk-Setup, Cloud-Lösungen, Microsoft 365 und professioneller Support',
          link: 'Mehr erfahren'
        }
      },
      features: {
        title: 'Warum Martinez IT Systems?',
        feature1: {
          title: 'Zuverlässig',
          description: 'Schnelle Reaktionszeiten und professionelle Lösungen für alle IT-Probleme'
        },
        feature2: {
          title: 'Erfahren',
          description: 'Langjährige Expertise in Computer-Systemen, Netzwerken und Cloud-Technologien'
        },
        feature3: {
          title: 'Persönlich',
          description: 'Individuelle Beratung und Support, perfekt auf Ihre Bedürfnisse zugeschnitten'
        }
      },
      cta: {
        title: 'Bereit für professionellen IT-Support?',
        description: 'Kontaktieren Sie mich noch heute für ein unverbindliches Beratungsgespräch',
        button: 'Kontakt aufnehmen'
      }
    },
    en: {
      hero: {
        title: 'Professional IT Solutions',
        subtitle: 'for Private Customers & Businesses',
        description: 'Martinez IT Systems – Your reliable partner for modern IT services. From installation to maintenance, from Windows 11 to cloud solutions.',
        cta: 'Contact Us Now',
        learnMore: 'Learn More'
      },
      services: {
        title: 'My Services',
        subtitle: 'Tailored IT services for your needs',
        private: {
          title: 'For Private Customers',
          description: 'Windows 11 installation, PC setup, virus protection, WiFi setup and smart home support',
          link: 'Learn more'
        },
        business: {
          title: 'For Businesses',
          description: 'System maintenance, network setup, cloud solutions, Microsoft 365 and professional support',
          link: 'Learn more'
        }
      },
      features: {
        title: 'Why Martinez IT Systems?',
        feature1: {
          title: 'Reliable',
          description: 'Fast response times and professional solutions for all IT problems'
        },
        feature2: {
          title: 'Experienced',
          description: 'Years of expertise in computer systems, networks and cloud technologies'
        },
        feature3: {
          title: 'Personal',
          description: 'Individual consultation and support, perfectly tailored to your needs'
        }
      },
      cta: {
        title: 'Ready for Professional IT Support?',
        description: 'Contact me today for a no-obligation consultation',
        button: 'Get in Touch'
      }
    }
  };

  const t = translations[language];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden bg-gray-900">
        {/* Background Image - Protected from Dark Mode Extensions */}
        <img 
          src="/images/big_banner_pexels-photo-7988743.jpeg"
          alt="IT Systems Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
          style={{
            opacity: '1 !important',
            filter: 'none !important',
            minHeight: '100%',
            minWidth: '100%'
          }}
        />
        <div 
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            background: 'linear-gradient(to right, rgba(0,0,0,0.7), rgba(0,0,0,0.5))',
            mixBlendMode: 'normal'
          }}
        ></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
              {t.hero.title}
              <span className="block mt-2" style={{ color: '#E53D4A' }}>
                {t.hero.subtitle}
              </span>
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              {t.hero.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-[#E53D4A] hover:bg-[#d63340] text-white"
                asChild
              >
                <a href="mailto:kontakt@martinez.systems">
                  {t.hero.cta}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="bg-white text-black hover:bg-gray-100 dark:bg-gray-900 dark:text-white dark:border-gray-500 dark:hover:bg-gray-800"
                asChild
              >
                <Link to="/prices">
                  {t.hero.learnMore}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#E53D4A' }}>
              {t.services.title}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              {t.services.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div 
                className="h-48 bg-cover bg-center"
                style={{
                  backgroundImage: 'url(/images/private_services_pexels-photo-842554.jpeg)'
                }}
              ></div>
              <CardHeader>
                <CardTitle style={{ color: '#E53D4A' }}>
                  {t.services.private.title}
                </CardTitle>
                <CardDescription className="text-base">
                  {t.services.private.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" asChild>
                  <Link to="/private-services">
                    {t.services.private.link}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div 
                className="h-48 bg-cover bg-center"
                style={{
                  backgroundImage: 'url(/images/business_services_unsplash-photo-1759884247194-f1fd2144951b.jpeg)'
                }}
              ></div>
              <CardHeader>
                <CardTitle style={{ color: '#E53D4A' }}>
                  {t.services.business.title}
                </CardTitle>
                <CardDescription className="text-base">
                  {t.services.business.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" asChild>
                  <Link to="/business-services">
                    {t.services.business.link}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-16" style={{ color: '#E53D4A' }}>
            {t.features.title}
          </h2>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#E53D4A] text-white mb-4">
                <Zap className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: '#E53D4A' }}>
                {t.features.feature1.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {t.features.feature1.description}
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#E53D4A] text-white mb-4">
                <Shield className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: '#E53D4A' }}>
                {t.features.feature2.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {t.features.feature2.description}
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#E53D4A] text-white mb-4">
                <Users className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: '#E53D4A' }}>
                {t.features.feature3.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {t.features.feature3.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#E53D4A] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            {t.cta.title}
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            {t.cta.description}
          </p>
          <Button 
            size="lg" 
            className="bg-white text-[#E53D4A] hover:bg-gray-100"
            asChild
          >
            <a href="mailto:kontakt@martinez.systems">
              {t.cta.button}
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
